import React from 'react';
import { FaShieldAlt, FaUsers, FaChartLine, FaAward, FaHeart, FaLightbulb } from 'react-icons/fa';

const About: React.FC = () => {
  return (
    <>
      {/* JSON-LD SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About Us - SpinTitanium",
            "description": "Learn about SpinTitanium's mission to provide honest, comprehensive casino reviews for UK players.",
            "url": "https://spintitanium.com/about",
            "mainEntity": {
              "@type": "Organization",
              "name": "SpinTitanium",
              "description": "UK's leading casino review platform"
            }
          })
        }}
      />

      <div className="min-h-screen bg-titanium-950 pt-20">
        {/* Hero */}
        <section className="bg-gradient-to-br from-titanium-900 to-titanium-950 py-20">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              <span className="text-gradient">About SpinTitanium</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              We're on a mission to make online casino gaming safer, more transparent, and more rewarding for UK players.
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-20 bg-titanium-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Our Mission</h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                SpinTitanium was born from a simple idea: players deserve honest, comprehensive information 
                before they deposit their money. Too many review sites prioritize affiliate commissions over 
                player welfare, leaving gamers in the dark about what they're really signing up for.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                We test every casino ourselves. We play the games, process withdrawals, contact support, 
                and verify licenses. Our reviews are based on real experiences, not marketing materials. 
                When we recommend a casino, it's because we've verified it meets our strict standards for 
                security, fairness, and player satisfaction.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Your trust is our most valuable asset. That's why we're transparent about our testing 
                methods, our affiliate relationships, and our editorial independence. We make money when 
                you sign up through our links, but we never let that influence our reviews.
              </p>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-20 bg-titanium-950">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-12 text-center">
              <span className="text-gradient">Our Core Values</span>
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: FaShieldAlt,
                  title: 'Transparency',
                  description: 'We disclose all affiliate relationships and testing methods. No hidden agendas, no misleading information.'
                },
                {
                  icon: FaUsers,
                  title: 'Player First',
                  description: 'Every review prioritizes player safety and satisfaction. We test from a player\'s perspective, not a marketer\'s.'
                },
                {
                  icon: FaChartLine,
                  title: 'Data-Driven',
                  description: 'Our recommendations are backed by real testing data, not assumptions. We track withdrawal times, test games, and verify licenses.'
                },
                {
                  icon: FaAward,
                  title: 'Excellence',
                  description: 'We continuously improve our review process and update our information. The casino industry changes fast, and we keep pace.'
                },
                {
                  icon: FaHeart,
                  title: 'Responsible Gaming',
                  description: 'We promote safe gambling practices and provide resources for players who need help. Gaming should be fun, not harmful.'
                },
                {
                  icon: FaLightbulb,
                  title: 'Innovation',
                  description: 'We use the latest tools and techniques to provide the most accurate, up-to-date information possible.'
                }
              ].map((value, index) => (
                <div
                  key={index}
                  className="glass rounded-xl p-8 border-2 border-titanium-800 hover:border-neon-blue transition-all"
                >
                  <value.icon className="text-5xl text-neon-blue mb-4" />
                  <h3 className="text-2xl font-bold text-white mb-4">{value.title}</h3>
                  <p className="text-gray-400">{value.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Team */}
        <section className="py-20 bg-titanium-900">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                <span className="text-gradient">Who We Are</span>
              </h2>
              <p className="text-gray-300 text-lg leading-relaxed mb-8">
                SpinTitanium is run by a team of experienced casino players, industry analysts, and 
                technology experts. We combine decades of gaming experience with rigorous testing 
                methodologies to deliver reviews you can trust.
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                Our team includes former casino employees, professional gamblers, and software engineers. 
                This diverse background gives us unique insights into how casinos operate, what players 
                actually experience, and where potential issues might arise.
              </p>
            </div>
          </div>
        </section>

        {/* Contact */}
        <section className="py-20 bg-titanium-950">
          <div className="container mx-auto px-4">
            <div className="max-w-2xl mx-auto glass rounded-2xl p-8 border-2 border-titanium-800 text-center">
              <h2 className="text-3xl font-bold text-white mb-4">
                <span className="text-gradient">Get In Touch</span>
              </h2>
              <p className="text-gray-300 mb-6">
                Have questions about our reviews? Found an issue with a casino? Want to suggest a casino 
                for us to review? We'd love to hear from you.
              </p>
              <a
                href="mailto:contact@spintitanium.com"
                className="inline-block bg-gradient-to-r from-neon-blue to-neon-purple hover:from-neon-purple hover:to-neon-pink text-white font-bold py-3 px-8 rounded-lg transition-all duration-300 transform hover:scale-105"
              >
                Contact Us
              </a>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default About;


